// The signed-in console frame (WEB_PLAN.md §1.1): a left navigation rail with the company name and role at the top,
// the sync indicator and the account menu at the bottom, and the page in a scrollable main area.
// - >= 1280px: labelled rail. 768–1279px: icon-only rail with tooltips. < 768px: a top bar whose menu button opens
//   the labelled rail as a drawer.
// - Reps get the reduced console (§3.1): "My numbers", "My stores", "My follow-ups", Documents and Account; any other
//   console page shows "Use the Ground Team app" instead of the screen.
// - Mounts the ToastProvider for every screen inside it.

import { MapPin, Menu, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { useSession } from '@/auth/AuthProvider';
import { USER_ROLE_TITLES } from '@/contracts/enums';
import type { Role } from '@/contracts/wire';
import { env } from '@/env';
import { canSeeRoute } from '@/session/permissions';
import { Chip, Dialog, IconButton, ToastProvider, Tooltip, cn, useMediaQuery } from '@/ui';
import { AccountMenu } from './AccountMenu';
import { isNavItemActive, navGroupsFor, type NavItem } from './nav';
import { SyncIndicator } from './SyncIndicator';
import { UseTheApp } from './UseTheApp';

/** Role chip colors from the iOS Settings header (SettingsView.roleColor). */
const ROLE_COLOR: Record<Role, string> = {
  admin: '#F96A3C',
  manager: '#7D4DBF',
  rep: '#2673E6',
};

const DESKTOP = '(min-width: 1280px)';
const TABLET = '(min-width: 768px)';

export default function AppShell() {
  const isDesktop = useMediaQuery(DESKTOP);
  const isTablet = useMediaQuery(TABLET);
  const { pathname } = useLocation();
  const session = useSession();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  // The main area is its own scroll container, so the browser does not reset it on navigation; do it here.
  // Also close the phone drawer once a link in it was followed.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    setDrawerOpen(false);
  }, [pathname]);

  // Growing past the phone breakpoint while the drawer is open would leave a modal over the rail.
  useEffect(() => {
    if (isTablet) setDrawerOpen(false);
  }, [isTablet]);

  const allowed = canSeeRoute(session.role, pathname);

  return (
    <ToastProvider>
      <a
        href="#gt-main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[var(--gt-z-toast)] focus:rounded-control focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-pop"
      >
        Skip to content
      </a>
      <div className="flex h-dvh w-full overflow-hidden bg-canvas text-fg">
        {isTablet && (
          <div
            className={cn(
              'relative z-[var(--gt-z-rail)] flex shrink-0 flex-col border-r border-line bg-surface',
              'transition-[width] duration-200 ease-out',
              isDesktop ? 'w-[var(--gt-rail-width)]' : 'w-[var(--gt-rail-width-compact)]',
            )}
          >
            <Rail compact={!isDesktop} pathname={pathname} />
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {!isTablet && <TopBar companyName={session.companyName} onMenu={() => setDrawerOpen(true)} />}
          <main
            id="gt-main"
            ref={mainRef}
            tabIndex={-1}
            className="relative min-h-0 flex-1 overflow-y-auto outline-none gt-scroll"
          >
            {allowed ? <Outlet /> : <UseTheApp />}
          </main>
        </div>
      </div>

      {!isTablet && (
        <Dialog
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="Menu"
          hideTitle
          placement="left"
          size="sm"
          flush
          bodyClassName="flex flex-col"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <Rail compact={false} pathname={pathname} inDrawer />
          </div>
        </Dialog>
      )}
    </ToastProvider>
  );
}

function TopBar({ companyName, onMenu }: { companyName: string; onMenu: () => void }) {
  return (
    <header
      className="flex shrink-0 items-center gap-2 border-b border-line bg-surface/95 px-2 backdrop-blur"
      style={{ height: 'var(--gt-topbar-height)' }}
    >
      <IconButton label="Open menu" icon={<Menu />} onClick={onMenu} />
      <AppMark size={28} />
      <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-fg">{companyName}</span>
      <SyncIndicator compact direction="row" />
    </header>
  );
}

function AppMark({ size = 36 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="gt-brand-gradient flex shrink-0 items-center justify-center rounded-[10px] text-white shadow-primary"
      style={{ width: size, height: size }}
    >
      <MapPin style={{ width: size * 0.55, height: size * 0.55 }} strokeWidth={2.4} />
    </span>
  );
}

function Rail({ compact, pathname, inDrawer = false }: { compact: boolean; pathname: string; inDrawer?: boolean }) {
  const session = useSession();
  const groups = navGroupsFor(session.role);
  const roleTitle = USER_ROLE_TITLES[session.role];
  const isRep = session.role === 'rep';

  const identity = (
    <div className={cn('flex items-center gap-3', compact ? 'justify-center' : 'min-w-0')}>
      <AppMark />
      {!compact && (
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] leading-tight font-bold text-fg" title={session.companyName}>
            {session.companyName}
          </p>
          <div className="mt-1 flex items-center gap-1.5">
            <Chip color={ROLE_COLOR[session.role]} filled size="xs">
              {roleTitle}
            </Chip>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={cn('shrink-0 gt-hero-soft border-b border-line', compact ? 'px-2 py-4' : 'px-4 py-4', inDrawer && 'pt-5')}>
        {compact ? (
          <>
            <Tooltip content={`${session.companyName} · ${roleTitle}`} placement="right" describe={false}>
              <div>{identity}</div>
            </Tooltip>
            <span className="sr-only">
              {session.companyName}, {roleTitle}
            </span>
          </>
        ) : (
          identity
        )}
      </div>

      <nav
        aria-label="Main navigation"
        className={cn('min-h-0 flex-1 overflow-y-auto gt-scroll', compact ? 'px-2 py-3' : 'px-3 py-3')}
      >
        {groups.map((group) => (
          <div key={group.id} className="mb-3 last:mb-0">
            {compact ? (
              <div aria-hidden className="mx-auto my-2 h-px w-6 bg-line first:hidden" />
            ) : (
              <p className="px-3 pt-1 pb-1.5 text-[11px] font-semibold tracking-[0.08em] text-fg-subtle uppercase">
                {group.label}
              </p>
            )}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.route}>
                  <RailLink item={item} compact={compact} active={isNavItemActive(item, pathname)} />
                </li>
              ))}
            </ul>
          </div>
        ))}

        {isRep && !compact && (
          <div className="mt-4 rounded-tile border border-line bg-surface-2 p-3 text-xs text-fg-muted">
            <div className="mb-1 flex items-center gap-1.5 font-semibold text-fg">
              <Smartphone aria-hidden className="size-3.5" />
              In the field?
            </div>
            Your map, visits and routes are in the Ground Team app.{' '}
            <a href={env.appStoreUrl} target="_blank" rel="noreferrer" className="font-semibold text-link hover:underline">
              Get the app
            </a>
          </div>
        )}
      </nav>

      <div className={cn('shrink-0 border-t border-line', compact ? 'flex flex-col items-center gap-2 px-2 py-3' : 'px-3 py-3')}>
        <SyncIndicator compact={compact} className={compact ? undefined : 'mb-2 px-2'} />
        <AccountMenu compact={compact} />
      </div>
    </div>
  );
}

function RailLink({ item, compact, active }: { item: NavItem; compact: boolean; active: boolean }) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      aria-label={compact ? item.label : undefined}
      className={cn(
        'group relative flex items-center rounded-control text-sm font-medium transition-colors',
        compact ? 'mx-auto size-11 justify-center' : 'h-10 gap-3 px-3',
        active ? 'bg-primary-soft font-semibold text-fg' : 'text-fg-muted hover:bg-surface-3/70 hover:text-fg',
      )}
    >
      {active && !compact && (
        <span aria-hidden className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full bg-primary" />
      )}
      <Icon
        aria-hidden
        className={cn('size-[18px] shrink-0', active ? 'text-primary-text' : 'text-fg-subtle group-hover:text-fg-muted')}
        strokeWidth={active ? 2.3 : 2}
      />
      {!compact && <span className="truncate">{item.label}</span>}
    </Link>
  );
  if (!compact) return link;
  return (
    <Tooltip content={item.label} placement="right" describe={false}>
      {link}
    </Tooltip>
  );
}
